import React, {useState} from 'react';
import {gql, useMutation, useQuery} from '@apollo/client';
import {Box, Button, Grid, Typography} from '@mui/material';
import moment from 'moment';
import {ListContacts_contacts} from '../../schema-types/ListContacts';
import ActionModal from './ActionModal';
import Loading from '../Loading/Loading';
import {
  GetContactNotifications,
  GetContactNotifications_getNotifications,
  GetContactNotificationsVariables,
} from '../../schema-types/GetContactNotifications';
import ErrorMessage from '../ErrorMessage/ErrorMessage';
import {
  DeleteContactNotifications,
  DeleteContactNotificationsVariables,
} from '../../schema-types/DeleteContactNotifications';
import {ScrollableModal} from '../Common/ScrollableModal';

export interface ManageContactNotificationsModalProps {
  contact: ListContacts_contacts;
  isOpen: boolean;
  close: () => void;
}

export const ManageContactNotificationsModal: React.FC<
  ManageContactNotificationsModalProps
> = ({contact, isOpen, close}) => {
  return (
    <ActionModal
      wide
      title={`Manage ${contact.simplifiedName} portal notifications`}
      isOpen={isOpen}
      close={close}
    >
      <ManageContactNotifications contact={contact} />
    </ActionModal>
  );
};

const getContactNotificationsQuery = gql`
  query GetContactNotifications($contactId: UUID!) {
    getNotifications(contactId: $contactId) {
      id
      createdAt
      updatedAt
      Message
    }
  }
`;

const deleteContactNotificationsMutation = gql`
  mutation DeleteContactNotifications($notificationIds: [UUID]) {
    deletePortalNotifications(notificationIds: $notificationIds) {
      ok
    }
  }
`;

export const ManageContactNotifications: React.FC<{contact: ListContacts_contacts}> = ({
  contact,
}) => {
  const {data, loading} = useQuery<
    GetContactNotifications,
    GetContactNotificationsVariables
  >(getContactNotificationsQuery, {
    variables: {
      contactId: contact.id,
    },
  });

  if (loading) {
    return <Loading entityName="notifications" />;
  }
  if (!data?.getNotifications) {
    return <ErrorMessage error={{message: 'Unable to get client notifications'}} />;
  }
  return (
    <Box>
      <Grid direction="column" p={1} spacing={1} container>
        {data?.getNotifications.length === 0 ? (
          <Typography>No client notifications</Typography>
        ) : null}
        {data?.getNotifications?.map((notification) => {
          if (!notification) {
            return;
          }
          return (
            <SingleNotificationDisplay
              key={notification.id}
              contact={contact}
              notification={notification}
            />
          );
        })}
      </Grid>
    </Box>
  );
};

export const SingleNotificationDisplay: React.FC<{
  notification: GetContactNotifications_getNotifications;
  contact: ListContacts_contacts;
}> = ({notification, contact}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [onDelete, {loading: deleteLoading}] = useMutation<
    DeleteContactNotifications,
    DeleteContactNotificationsVariables
  >(deleteContactNotificationsMutation, {
    refetchQueries: [
      {
        query: getContactNotificationsQuery,
        variables: {
          contactId: contact.id,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  return (
    <>
      <ScrollableModal open={showDeleteModal} onClose={() => setShowDeleteModal(false)}>
        <>
          <Typography mb={2}>
            Are you sure you want to delete this notification:
          </Typography>
          <Grid container direction="row" alignContent="center" spacing={1}>
            <Grid width={200} item>
              <Typography>
                {moment(notification?.createdAt).format('MMMM DD, YYYY HH:MMa')}
              </Typography>
            </Grid>
            <Grid flex={1} item>
              <Typography>{notification?.Message}</Typography>
            </Grid>
          </Grid>
          <Grid mt={2} container direction="row" alignContent="center" spacing={1}>
            <Button
              disabled={deleteLoading}
              onClick={() => {
                setShowDeleteModal(false);
              }}
              variant="outlined"
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              disabled={deleteLoading}
              onClick={() => {
                onDelete({
                  variables: {
                    notificationIds: [notification.id ?? ''],
                  },
                }).then(() => {
                  setShowDeleteModal(false);
                });
              }}
              variant="outlined"
              color="secondary"
            >
              Delete
            </Button>
          </Grid>
        </>
      </ScrollableModal>

      <Grid key={notification?.id} item mb={1} pb={1} borderBottom="1px solid lightgrey">
        <Grid container direction="row" alignContent="center" spacing={1}>
          <Grid width={200} item>
            <Typography>
              {moment(notification?.createdAt).format('MMMM DD, YYYY HH:MMa')}
            </Typography>
          </Grid>
          <Grid flex={1} item>
            <Typography>{notification?.Message}</Typography>
          </Grid>
          <Grid item>
            <Button
              disabled={deleteLoading}
              onClick={() => {
                setShowDeleteModal(true);
              }}
              variant="outlined"
              color="secondary"
            >
              Delete
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
